import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from '@heroicons/react/20/solid'
import React, { useState } from 'react'
import Calendar from 'react-calendar'
import { useFormContext } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { BookShelfForm } from '@/pages/customer/book-shelf'
import { format, isSameDay, isSunday, isToday, parseISO } from 'date-fns'
import { toast } from 'sonner'

import { CalendarIcon } from '@/assets/icons'
import { PACKAGES } from '@/constants'
import { cn } from '@/lib/utils'
import holidayService from '@/services/holiday-service'
import standService from '@/services/stand-service'

import bookingService from '@/services/booking-service'
import '@/styles/calendar.css'
import 'react-calendar/dist/Calendar.css'

export const RegalForm = () => {
	const form = useFormContext<BookShelfForm>()
	const [date, setDate] = useState<CalendarValue>()
	const [disabledDates, setDisabledDates] = useState<Holiday[]>([])
	const [shelfList, setShelfList] = useState<StandStatus[]>([])

	const selectedPackage = PACKAGES.find(p => form.watch('package') === p?.id.toString())

	useQuery('fetch-all-holidays', holidayService.fetchAllHolidays, {
		enabled: !!selectedPackage,
		onSuccess: res => {
			setDisabledDates(res.data)

			const startDate = new Date()
			startDate.setDate(startDate.getDate() + 1)
			startDate.setHours(0, 0, 0, 0)

			const endDate = calcWorkingDays(new Date(startDate), selectedPackage!.duration, res.data)
			setDate([
				form.watch('startDate') ?? new Date(startDate),
				form.watch('endDate') ?? new Date(endDate),
			])
			if (!form.watch('startDate')) {
				form.setValue('startDate', new Date(startDate))
			}
			if (!form.watch('endDate')) {
				form.setValue('endDate', new Date(endDate))
			}
			loadShelfData({
				startDate: form.watch('startDate') ?? new Date(startDate),
				endDate: form.watch('endDate') ?? new Date(endDate),
			})
		},
		onError: () => {
			toast.error('Error fetching holidays')
		},
	})

	const { mutate } = useMutation({
		mutationKey: 'verify-stand-availibility',
		mutationFn: ({ standId, start, end }: { standId: number; start: string; end: string }) =>
			bookingService.verifyStandAvailability(standId, start, end),
		onError: () => {
			form.setValue('stand', undefined as any)
			toast.error('Stand already booked. Please select another stand')
		},
	})

	const { mutate: loadShelfData } = useMutation({
		mutationKey: 'stand-status',
		mutationFn: (data: { startDate: Date; endDate: Date }) => {
			const startUTC = new Date(
				Date.UTC(data.startDate.getFullYear(), data.startDate.getMonth(), data.startDate.getDate()),
			)
			startUTC.setUTCHours(0, 0, 0, 0)

			const endUTC = new Date(
				Date.UTC(data.endDate.getFullYear(), data.endDate.getMonth(), data.endDate.getDate()),
			)
			endUTC.setUTCHours(23, 59, 0, 0)

			return standService.fetchStandStatus(startUTC, endUTC)
		},
		onSuccess: res => {
			const excludedNumbers = new Set([
				19, 20, 95, 138, 139, 154, 155, 156, 157, 158, 159, 160, 161, 162, 163,
			])

			const filteredStands = res.filter(({ label }) => {
				const numLabel = Number(label)
				return (
					Number.isNaN(numLabel) ||
					(!excludedNumbers.has(numLabel) && (numLabel < 59 || numLabel > 94))
				)
			})
			setShelfList(filteredStands)
		},
		onError: () => {
			toast.error('Error fetching stands')
		},
	})

	const calcWorkingDays = (fromDate: Date, days: number, holidays = disabledDates) => {
		let count = 1
		const workingDate = new Date(fromDate)

		while (count < days) {
			workingDate.setDate(workingDate.getDate() + 1)

			if (
				workingDate.getDay() !== 0 &&
				!holidays.find(dDate => isSameDay(parseISO(dDate.holiday_date), workingDate))
			) {
				count++
			}
		}

		return workingDate
	}

	const onDaySelect = (value: any) => {
		const workingDays = calcWorkingDays(new Date(value), selectedPackage!.duration)

		setDate([new Date(value), new Date(workingDays)])
		form.setValue('startDate', new Date(value))
		form.setValue('endDate', workingDays)
		form.setValue('stand', undefined as any)
		loadShelfData({ startDate: new Date(value), endDate: workingDays })
	}

	const tileDisabled = ({ date }: { date: Date }) => {
		if (isSunday(new Date(date))) {
			return true
		} else if (isToday(new Date(date))) {
			return true
		} else {
			return !!disabledDates.find(dDate => isSameDay(new Date(dDate?.holiday_date), date))
		}
	}
	return (
		<div className="mt-9 lg:mt-12 flex flex-col">
			<h5 className="text-secondary/50 max-lg:text-xs font-medium">
				Wähle deinen gewünschten Zeitraum
			</h5>
			<div className="mt-6 flex max-lg:flex-col items-stretch gap-3">
				<Calendar
					className="custom-calendar lg:basis-[70%]"
					onChange={onDaySelect}
					value={date}
					selectRange={false}
					prevLabel={
						<ChevronLeftIcon className="size-3 max-lg:ml-5 lg:size-5 shrink-0 text-secondary/50" />
					}
					nextLabel={
						<ChevronRightIcon className="size-3 max-lg:mr-5 lg:size-5 shrink-0 text-secondary/50" />
					}
					prev2Label={
						<ChevronDoubleLeftIcon className="size-3 lg:size-5 shrink-0 text-secondary/50" />
					}
					next2Label={
						<ChevronDoubleRightIcon className="size-3 lg:size-5 shrink-0 text-secondary/50" />
					}
					formatShortWeekday={(locale, date) =>
						date.toLocaleDateString(locale, { weekday: 'short' })
					}
					minDetail="year"
					minDate={new Date()}
					allowPartialRange={true}
					tileDisabled={({ date, view }) => {
						return tileDisabled({ date }) && view === 'month'
					}}
					goToRangeStartOnSelect={true}
				/>
				<div className="flex grow lg:flex-col lg:basis-[30%] gap-2 lg:gap-3">
					<div className="border border-[#ECEEF3] rounded-xl p-3 lg:px-4 lg:py-6 flex flex-col gap-4">
						<div className="flex items-center gap-2">
							<div className="size-5 bg-[#ECECEC] rounded" />
							<p className="text-secondary/50 font-medium text-xs lg:text-sm">Nicht verfügbar</p>
						</div>
						<div className="flex items-center gap-2">
							<div className="size-5 bg-[#327B74] rounded" />
							<p className="text-secondary/50 font-medium text-xs lg:text-sm">Deine Auswahl</p>
						</div>
						<div className="flex items-center gap-2">
							<div className="size-5 bg-[#F6E8DE] rounded" />
							<p className="text-secondary/50 font-medium text-xs lg:text-sm">
								Ausgewählter Zeitraum
							</p>
						</div>
					</div>
					<div className="border grow border-[#ECEEF3] rounded-xl p-3 lg:py-6 lg:px-4 flex flex-col">
						<div className="flex items-center gap-1">
							<CalendarIcon />
							<p className="text-secondary/50 max-lg:text-xs font-medium text-sm">Deine Buchung</p>
						</div>
						{form.watch('startDate') && form.watch('endDate') && (
							<div className="flex items-center flex-col gap-1 lg:gap-3.5 mt-3.5 lg:mt-[28px]">
								<h1 className="text-secondary font-extrabold text-xs lg:text-2xl">
									{format(form.watch('startDate'), 'dd.MM.yyyy')}
								</h1>
								<p className="text-secondary/50 text-xs font-medium">bis</p>
								<h1 className="text-secondary font-extrabold text-xs lg:text-2xl">
									{format(form.watch('endDate'), 'dd.MM.yyyy')}
								</h1>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="mt-10 lg:mt-16">
				<h5 className="text-secondary/50 max-lg:text-xs font-medium">
					Wähle jetzt das Regal deiner Wahl
				</h5>
				<div className="mt-6 w-full overflow-x-auto -mx-1 px-1">
					<svg
						viewBox="0 0 980 600"
						preserveAspectRatio="xMidYMid meet"
						className="mx-auto block h-auto w-full min-w-[640px] max-w-[980px] -mt-12 sm:-mt-16 lg:-mt-[100px]">
						{shelfList.map(item => (
							<StandBox
								key={item.id}
								onSelection={standId =>
									mutate({
										standId,
										start: format(form.watch('startDate'), 'yyyy-LL-dd'),
										end: format(form.watch('endDate'), 'yyyy-LL-dd'),
									})
								}
								fill={
									item.status === 'active'
										? item.is_available
											? '#327B74'
											: '#ECECEC'
										: item.label === 'Kasse' ||
											  item.label === 'Eingang' ||
											  item.label === 'WC / Still- und Wickelraum'
											? '#F5F7F8'
											: 'white'
								}
								{...item}
							/>
						))}
					</svg>
				</div>
			</div>
		</div>
	)
}

interface StandBoxProps extends StandStatus {
	fill: string
	onSelection: (standId: number) => void
}

const StandBox: React.FC<StandBoxProps> = ({
	width,
	height,
	x,
	y,
	fill,
	label,
	id,
	is_available,
	onSelection,
	status,
}) => {
	const form = useFormContext<BookShelfForm>()

	let shapeElement = (
		<rect
			id={`shelf_${id}`}
			data-shelf_id={`${id}`}
			data-shelf_label={`${label}`}
			x={x}
			y={y}
			stroke={
				label !== 'Kasse' &&
				label !== 'Eingang' &&
				label !== 'WC / Still- und Wickelraum' &&
				is_available
					? 'white'
					: ''
			}
			strokeWidth="1"
			rx="8"
			ry="8"
		/>
	)

	const styleProps: React.CSSProperties = {
		fill: form.watch('stand')?.id === id ? '#FFFF6E' : fill,
		width,
		height,
	}

	return (
		<g
			transform="matrix(1,0,0,1,0,0)"
			className={cn(
				status === 'Disabled'
					? 'hidden'
					: status === 'active'
						? 'cursor-pointer'
						: 'cursor-not-allowed animate',
			)}
			role="button"
			onClick={() => {
				if (is_available) {
					form.setValue('stand', { id, label, x, y, is_available })
					onSelection(id)
				}
			}}>
			{React.cloneElement(shapeElement, { style: styleProps })}
			<text
				x={x + width / 2}
				y={y + 3}
				fill={
					form.watch('stand')?.id === id
						? 'black'
						: !is_available
							? '#ABABAB'
							: label !== 'Kasse' && label !== 'Eingang' && label !== 'WC / Still- und Wickelraum'
								? 'white'
								: '#0B224D80'
				}
				fontFamily="Helvetica"
				fontSize="12"
				textAnchor="middle">
				<tspan dy="16" x={x + width / 2}>
					{label}
				</tspan>
			</text>
		</g>
	)
}
